/**
 * Selection → Stability v2beta inpaint mask helpers.
 *
 * Stability Stable Image Edit /inpaint (REST v2beta):
 *   white pixels = regions to inpaint (edit)
 *   black pixels = regions to preserve
 *
 * Editor selection mask: mask[i] === 1 means selected (to edit).
 * Therefore selected → white (255), unselected → black (0).
 *
 * NOTE: Legacy Stability gRPC docs flipped this polarity. Do not use those.
 */

/**
 * @typedef {{ width: number, height: number, mask: Uint8Array }} EditorSelection
 */

/**
 * Dilate a binary selection mask by radiusPx (Chebyshev / square kernel).
 * Used for optional soft blend edges. Default product choice: OFF (radius 0).
 *
 * @param {EditorSelection|null|undefined} selection
 * @param {number} [radiusPx=2]
 * @returns {EditorSelection|null}
 */
export function dilateSelectionMask(selection, radiusPx = 2) {
  if (!selection || !selection.mask) return null;
  const w = selection.width | 0;
  const h = selection.height | 0;
  const src = selection.mask;
  if (w <= 0 || h <= 0 || src.length < w * h) return null;
  const r = Math.max(0, Math.min(8, Math.round(Number(radiusPx) || 0)));
  if (r === 0) {
    return {
      width: w,
      height: h,
      mask: new Uint8Array(src),
    };
  }
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let on = 0;
      for (let dy = -r; dy <= r && !on; dy += 1) {
        for (let dx = -r; dx <= r; dx += 1) {
          const sx = x + dx;
          const sy = y + dy;
          if (sx < 0 || sy < 0 || sx >= w || sy >= h) continue;
          if (src[sy * w + sx]) {
            on = 1;
            break;
          }
        }
      }
      out[y * w + x] = on;
    }
  }
  return { width: w, height: h, mask: out };
}

/**
 * Build grayscale RGBA bytes for a Stability inpaint mask PNG.
 * Selected → white (255), unselected → black (0). Alpha always 255.
 *
 * @param {EditorSelection} selection
 * @returns {Uint8ClampedArray}
 */
export function selectionMaskToStabilityRgba(selection) {
  const w = selection.width | 0;
  const h = selection.height | 0;
  const src = selection.mask;
  const rgba = new Uint8ClampedArray(w * h * 4);
  for (let i = 0; i < w * h; i += 1) {
    const v = src[i] ? 255 : 0;
    const o = i * 4;
    rgba[o] = v;
    rgba[o + 1] = v;
    rgba[o + 2] = v;
    rgba[o + 3] = 255;
  }
  return rgba;
}

/**
 * True when any mask bit is set.
 * @param {EditorSelection|null|undefined} selection
 * @returns {boolean}
 */
export function selectionMaskHasPixels(selection) {
  if (!selection || !selection.mask) return false;
  const m = selection.mask;
  for (let i = 0; i < m.length; i += 1) {
    if (m[i]) return true;
  }
  return false;
}

/**
 * Encode selection as a PNG data URL for the callable (browser).
 * @param {EditorSelection} selection
 * @param {{ soften?: boolean, softenRadiusPx?: number }} [opts]
 * @returns {Promise<string>}
 */
export async function selectionMaskToStabilityPngDataUrl(selection, opts = {}) {
  if (!selectionMaskHasPixels(selection)) {
    throw new Error('Make a selection first');
  }
  const soften = opts.soften === true;
  const radius =
    typeof opts.softenRadiusPx === 'number' ? opts.softenRadiusPx : 2;
  const prepared = soften
    ? dilateSelectionMask(selection, radius)
    : {
        width: selection.width,
        height: selection.height,
        mask: selection.mask,
      };
  if (!prepared) throw new Error('Invalid selection mask.');
  const rgba = selectionMaskToStabilityRgba(prepared);
  const canvas = document.createElement('canvas');
  canvas.width = prepared.width;
  canvas.height = prepared.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not encode inpaint mask.');
  const imageData = new ImageData(
    rgba,
    prepared.width,
    prepared.height,
  );
  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not encode inpaint mask PNG.'));
          return;
        }
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Mask PNG read failed.'));
        reader.readAsDataURL(blob);
      },
      'image/png',
    );
  });
}

/**
 * Documented polarity constant for smokes / reviews.
 * Stability v2beta: white = inpaint.
 */
export const STABILITY_INPAINT_WHITE_MEANS = 'inpaint';
